/* Recording state-machine for the transcribe-web client.
 *
 * Ported from Point Hub Slice 19 recording.svelte.ts. Differences:
 *   - No CommandStore / IntentRecognizer leg — transcript is the final
 *     product, surfaced via store.lastResult for the host to read after
 *     stop() resolves.
 *   - apiTranscribe routes through src/lib/api.ts (direct or proxy mode
 *     depending on runtime config — see src/lib/config.ts).
 *   - JS-API exposed as window.transcribeWeb.recording, merged with any
 *     window.transcribeWeb.config the host may have injected.
 *   - No on-screen transcript and no history — the UI is a single mic
 *     button. Hosts pull the final state from getSnapshot() once stop()
 *     resolves; per-attempt logging is the host's responsibility.
 *
 * Defensive plumbing kept identical to Point Hub: _cleanup() at the
 * top of every start(), MediaRecorder.start(1000) timeslice,
 * requestData() before stop(), reject sub-600 ms blobs locally.
 */
import { apiTranscribe, ApiError, type TranscribeResult } from "./api";

export type RecState =
  | "idle"
  | "requesting"
  | "recording"
  | "stopping"
  | "sending"
  | "error";

export type RecError =
  | "permission_denied"
  | "no_speech"
  | "transcription_failed"
  | "rate_limited"
  | "unsupported"
  | "unknown";

class RecordingStore {
  state = $state<RecState>("idle");
  error = $state<RecError | null>(null);
  elapsedMs = $state(0);
  lastResult = $state<TranscribeResult | null>(null);

  private _stream: MediaStream | null = null;
  private _recorder: MediaRecorder | null = null;
  private _chunks: Blob[] = [];
  private _startedAt = 0;
  private _tickHandle: number | null = null;
  private _errorResetHandle: number | null = null;
  private static readonly ERROR_AUTO_RESET_MS = 3000;
  /* A hotkey tap can end before getUserMedia resolves. stop() during
   * "requesting" is remembered and applied once capture is up; cancel()
   * bumps the generation so the pending start() releases the microphone
   * instead of recording. */
  private _startGeneration = 0;
  private _stopRequested = false;

  /** Begin capture. */
  async start(): Promise<void> {
    if (this.state !== "idle" && this.state !== "error") return;
    this._cancelErrorReset();
    this.error = null;
    this.state = "requesting";
    this._stopRequested = false;
    const generation = ++this._startGeneration;

    if (typeof navigator === "undefined" ||
        !navigator.mediaDevices ||
        typeof MediaRecorder === "undefined") {
      this._fail("unsupported");
      return;
    }

    this._cleanup();

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (e) {
      if (generation !== this._startGeneration) return;
      const name = (e as DOMException).name;
      const code: RecError = name === "NotAllowedError" || name === "SecurityError"
        ? "permission_denied"
        : "unknown";
      this._fail(code);
      return;
    }
    if (generation !== this._startGeneration) {
      stream.getTracks().forEach((t) => {
        try { t.stop(); } catch { /* */ }
      });
      return;
    }
    this._stream = stream;

    try {
      this._recorder = new MediaRecorder(this._stream);
    } catch {
      this._stopStream();
      this._fail("unsupported");
      return;
    }

    this._chunks = [];
    this._recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) this._chunks.push(e.data);
    };
    this._recorder.start(1000);
    this._startedAt = Date.now();
    this.elapsedMs = 0;
    this._tickHandle = window.setInterval(() => {
      this.elapsedMs = Date.now() - this._startedAt;
    }, 200);
    this.state = "recording";

    if (this._stopRequested) {
      this._stopRequested = false;
      await this.stop();
    }
  }

  /** End capture, transcribe, surface the result. */
  async stop(): Promise<void> {
    if (this.state === "requesting") {
      this._stopRequested = true;
      return;
    }
    if (this.state !== "recording") return;
    this.state = "stopping";
    if (this._tickHandle !== null) {
      window.clearInterval(this._tickHandle);
      this._tickHandle = null;
    }

    await new Promise<void>((resolve) => {
      const rec = this._recorder;
      if (!rec || rec.state === "inactive") return resolve();
      rec.onstop = () => resolve();
      try { rec.requestData(); } catch { /* spec says safe; some builds throw */ }
      rec.stop();
    });
    this._stopStream();

    const mimeType = this._recorder?.mimeType || "audio/webm";
    const blob = new Blob(this._chunks, { type: mimeType });
    const ext = mimeType.includes("ogg") ? "ogg"
      : mimeType.includes("mp4") ? "mp4"
      : "webm";
    const filename = `voice.${ext}`;
    const elapsed = this.elapsedMs;
    this._chunks = [];
    this._recorder = null;

    if (typeof console !== "undefined" && console.debug) {
      console.debug(
        "[transcribe-web] stopped",
        { size: blob.size, type: mimeType, ms: elapsed },
      );
    }

    if (blob.size === 0 || elapsed < 600) {
      this._fail("no_speech");
      return;
    }

    this.state = "sending";

    try {
      const result = await apiTranscribe(blob, filename);
      if (!result.text || !result.text.trim()) {
        this._fail("no_speech");
        return;
      }
      this.lastResult = result;
      this.state = "idle";
      this._notifyHost();
    } catch (e) {
      const code: RecError =
        e instanceof ApiError && e.status === 429 ? "rate_limited"
        : "transcription_failed";
      this._fail(code);
    }
  }

  /** Abort current recording without sending anything. */
  cancel(): void {
    if (this.state === "idle") return;
    this._startGeneration++;
    this._stopRequested = false;
    if (this._recorder && this._recorder.state === "recording") {
      try { this._recorder.stop(); } catch { /* already inactive */ }
    }
    this._cleanup();
    this._cancelErrorReset();
    this.state = "idle";
    this.error = null;
  }

  /** Clear any error and return to idle. */
  reset(): void {
    this._cancelErrorReset();
    if (this.state === "error") this.state = "idle";
    this.error = null;
  }

  // ─── private ────────────────────────────────────────────────────────────

  private _fail(code: RecError): void {
    this.state = "error";
    this.error = code;
    this._notifyHost();
    this._scheduleErrorReset();
  }

  /** Auto-clear the visible error indication after a few seconds so the
   *  red mic doesn't linger. The error code stays in lastResult-equivalent
   *  channels (DebugView, host's already-received postMessage), only the
   *  on-screen state goes back to idle. A new start() cancels the timer. */
  private _scheduleErrorReset(): void {
    this._cancelErrorReset();
    if (typeof window === "undefined") return;
    this._errorResetHandle = window.setTimeout(() => {
      this._errorResetHandle = null;
      if (this.state === "error") {
        this.state = "idle";
        this.error = null;
      }
    }, RecordingStore.ERROR_AUTO_RESET_MS);
  }

  private _cancelErrorReset(): void {
    if (this._errorResetHandle !== null) {
      window.clearTimeout(this._errorResetHandle);
      this._errorResetHandle = null;
    }
  }

  /** Push a snapshot to the embedding host (Delphi WebView2 et al.) on every
   *  terminal transition out of stop(). The host listens via OnWebMessageReceived;
   *  this fires regardless of what triggered the stop — UI click, hotkey, or a
   *  third-party JS call to recording.stop(). In a plain browser tab there is no
   *  chrome.webview, so this is a no-op. */
  private _notifyHost(): void {
    if (typeof window === "undefined") return;
    const w = window as unknown as {
      chrome?: { webview?: { postMessage?(m: unknown): void } };
    };
    w.chrome?.webview?.postMessage?.({
      state:      this.state,
      error:      this.error,
      elapsedMs:  this.elapsedMs,
      lastResult: this.lastResult,
    });
  }

  private _stopStream(): void {
    this._stream?.getTracks().forEach((t) => {
      try { t.stop(); } catch { /* */ }
    });
    this._stream = null;
  }

  private _cleanup(): void {
    if (this._tickHandle !== null) {
      window.clearInterval(this._tickHandle);
      this._tickHandle = null;
    }
    this._stopStream();
    this._recorder = null;
    this._chunks = [];
  }
}

let _instance: RecordingStore | null = null;

export function recordingStore(): RecordingStore {
  if (!_instance) {
    _instance = new RecordingStore();
    if (typeof window !== "undefined") {
      const win = window as unknown as Record<string, unknown>;
      const root = (win.transcribeWeb as Record<string, unknown> | undefined) ?? {};
      win.transcribeWeb = {
        ...root,
        recording: {
          start:       () => _instance!.start(),
          stop:        () => _instance!.stop(),
          cancel:      () => _instance!.cancel(),
          getState:    () => _instance!.state,
          getSnapshot: () => ({
            state:      _instance!.state,
            error:      _instance!.error,
            elapsedMs:  _instance!.elapsedMs,
            lastResult: _instance!.lastResult,
          }),
        },
      };
    }
  }
  return _instance;
}
